import React, { useCallback, useEffect, useRef, useState } from 'react';

import clsx from 'clsx';
import { PanelTopHeader } from '@/components/panels/PanelTopHeader/PanelTopHeader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FormGate } from '../FormGate/FormGate';
import { IntegrationsZeroState } from '../IntegrationsZeroState/IntegrationsZeroState';
import { WorkflowList, type WorkflowWithProvider } from '../WorkflowList/WorkflowList';
import { AutomationSetup, type AutomationSetupStep } from '../AutomationSetup/AutomationSetup';
import { usePageHasForm } from '@/hooks/usePageHasForm';
import { getWorkflows, getWorkflow } from '@/services/workflowApi';
import { ASSETS_PANEL } from '@builder4/shared/utils/constants';
import { useAppSelector } from '@/store/hooks';
import { selectCurrentPageId } from '@/store/pages/selectors';
import { useMixpanel } from '@/hooks/useMixpanel';
import {
  VIEWED_INTEGRATIONS_PANEL,
  CLICKED_ADD_A_FORM,
  CLICKED_SET_UP_AUTOMATION,
  CLICKED_NEW_AUTOMATION,
  EDITED_AN_AUTOMATION,
} from '@/analytics/mixpanel/constants/events';
import { PANEL_STATE } from '@/analytics/mixpanel/constants/properties';

interface IntegrationsProps {
  className: string;
  onIntegrationsClick?: (elementName: string) => void;
}

type IntegrationsView =
  | { kind: 'list' }
  | { kind: 'setup'; workflowId?: string; initialStep?: AutomationSetupStep };

export const Integrations: React.FC<IntegrationsProps> = ({ className, onIntegrationsClick }) => {
  const hasForm = usePageHasForm();
  const currentPageId = useAppSelector(selectCurrentPageId);
  const track = useMixpanel();
  const [workflows, setWorkflows] = useState<WorkflowWithProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [workflowsFetched, setWorkflowsFetched] = useState(false);
  const [view, setView] = useState<IntegrationsView>({ kind: 'list' });
  const [autoNavigated, setAutoNavigated] = useState(false);
  const hasViewedPanelRef = useRef(false);

  const handleClick = (elementName: string) => {
    if (onIntegrationsClick) {
      onIntegrationsClick(elementName);
    }
  };

  const handleAddFormClick = () => {
    track(CLICKED_ADD_A_FORM);
    handleClick(ASSETS_PANEL.ELEMENTS);
  };

  const fetchWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWorkflows();
      const items = response._items ?? [];
      const visibleWorkflows = items.filter((w) => !w.hasError);

      const enriched = await Promise.all(
        visibleWorkflows.map(async (w): Promise<WorkflowWithProvider> => {
          try {
            const details = await getWorkflow(w._meta.id);
            const provider = details.destinationActionSettings?.[0]?.provider;
            const sourceIds = details.sourceIds ?? [];
            return { ...w, provider, sourceIds };
          } catch {
            return w;
          }
        }),
      );

      setWorkflows(enriched);
      setWorkflowsFetched(true);
    } catch {
      setWorkflows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasForm) {
      void fetchWorkflows();
    }
  }, [hasForm, fetchWorkflows]);

  useEffect(() => {
    if (hasViewedPanelRef.current) return;
    if (!hasForm) {
      track(VIEWED_INTEGRATIONS_PANEL, { [PANEL_STATE]: 'form_gate' });
      hasViewedPanelRef.current = true;
      return;
    }
    if (workflowsFetched && hasForm && workflows.length === 0 && !autoNavigated) {
      setAutoNavigated(true);
      setView({ kind: 'setup', initialStep: 'destination' });
      track(VIEWED_INTEGRATIONS_PANEL, { [PANEL_STATE]: 'zero_state' });
      hasViewedPanelRef.current = true;
      return;
    }
    if (workflowsFetched && hasForm && workflows.length > 0) {
      track(VIEWED_INTEGRATIONS_PANEL, { [PANEL_STATE]: 'automation_list' });
      hasViewedPanelRef.current = true;
    }
  }, [hasForm, workflowsFetched, workflows.length, track, autoNavigated]);

  const handleStartNewAutomation = () => {
    track(CLICKED_SET_UP_AUTOMATION);
    setView({ kind: 'setup' });
  };

  const handleNewAutomationFromList = () => {
    track(CLICKED_NEW_AUTOMATION);
    setView({ kind: 'setup' });
  };

  const handleEditAutomation = (workflowId: string) => {
    track(EDITED_AN_AUTOMATION);
    setView({ kind: 'setup', workflowId });
  };

  const handleExitSetup = () => {
    setView({ kind: 'list' });
    // Re-fetch so the list reflects any changes made during setup.
    void fetchWorkflows();
  };

  const handleClosePanel = () => {
    handleClick('');
  };

  if (view.kind === 'setup' && currentPageId) {
    return (
      <AutomationSetup
        currentPageId={currentPageId}
        workflowId={view.workflowId}
        initialStep={view.initialStep}
        onExitSetup={handleExitSetup}
        onClosePanel={handleClosePanel}
      />
    );
  }

  const renderContent = () => {
    if (!hasForm) {
      return <FormGate onAddFormClick={handleAddFormClick} />;
    }

    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
        </div>
      );
    }

    if (workflows.length === 0) {
      return <IntegrationsZeroState onSetupClick={handleStartNewAutomation} />;
    }

    return (
      <WorkflowList
        workflows={workflows}
        currentPageId={currentPageId}
        onWorkflowClick={handleEditAutomation}
        onNewAutomationClick={handleNewAutomationFromList}
      />
    );
  };

  return (
    <div className={clsx(className, 'flex h-full max-w-[280px] flex-col')}>
      <PanelTopHeader
        text="Integrations"
        backButton={false}
        onClickClose={() => handleClick('')}
        className={''}
        saveBtnShow={false}
      />

      <ScrollArea className="flex w-full flex-1 overflow-hidden px-4 py-4 [&_[data-slot=scroll-area-viewport]>div]:!block">
        {renderContent()}
      </ScrollArea>
    </div>
  );
};
